"use client";

import { c as _c } from "react/compiler-runtime";
import { canonicalizeInteractiveMediaUrl } from "@/components/waves/memes/submission/constants/security";
import React, { useEffect, useMemo, useRef, useState } from "react";

// Sandbox policy for external interactive media:
// - allow-scripts: Required for interactive HTML content.
// - intentionally omits allow-pointer-lock, allow-same-origin and allow-popups to preserve isolation and block window spawning.
const DEFAULT_SANDBOX = "allow-scripts";
interface SandboxedExternalIframeProps {
  readonly src: string;
  readonly title: string;
  readonly className?: string | undefined;
  readonly fallback?: React.ReactNode | undefined;
  readonly containerClassName?: string | undefined;
}

/**
 * Render untrusted interactive media inside a strongly sandboxed iframe.
 *
 * Security notes:
 * - keep allow-same-origin disabled to force an opaque origin boundary.
 * - deny all Permission Policy features via an explicit empty `allow` attribute.
 * - enforce HTTPS and strip referrers; credentialless removes ambient cookies where supported.
 * - the sandbox isolates the frame even if it redirects after the initial load.
 */
const SandboxedExternalIframe: React.FC<SandboxedExternalIframeProps> = t0 => {
  const $ = _c(32);
  const {
    src,
    title,
    className,
    fallback: t1,
    containerClassName
  } = t0;
  const fallback = t1 === undefined ? null : t1;
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  let t2;
  if ($[0] !== src) {
    t2 = canonicalizeInteractiveMediaUrl(src);
    $[0] = src;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const canonicalSrc = t2;
  let t3;
  if ($[2] !== className) {
    t3 = ["tw-h-full", "tw-w-full", className].filter(_temp);
    $[2] = className;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const classes = t3;
  const frameClassName = classes.join(" ");
  let t4;
  let t5;
  if ($[4] !== canonicalSrc || $[5] !== isVisible) {
    t4 = () => {
      if (!canonicalSrc) {
        return;
      }
      if (isVisible) {
        return;
      }
      const element = containerRef.current;
      if (!element) {
        return;
      }
      if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
        setIsVisible(true);
        return;
      }
      const observer = new IntersectionObserver((entries, observerInstance) => {
        const isIntersecting = entries.some(_temp2);
        if (isIntersecting) {
          setIsVisible(true);
          observerInstance.disconnect();
        }
      }, {
        root: null,
        threshold: 0.1
      });
      observer.observe(element);
      return () => {
        observer.disconnect();
      };
    };
    t5 = [canonicalSrc, isVisible];
    $[4] = canonicalSrc;
    $[5] = isVisible;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  bb0: {
    if (!canonicalSrc) {
      t6 = null;
      break bb0;
    }
    try {
      let t7;
      if ($[8] !== canonicalSrc) {
        t7 = new URL(canonicalSrc);
        $[8] = canonicalSrc;
        $[9] = t7;
      } else {
        t7 = $[9];
      }
      t6 = t7;
    } catch {
      t6 = null;
    }
  }
  const parsedCanonicalUrl = t6;
  let t7;
  bb1: {
    if (!canonicalSrc) {
      t7 = null;
      break bb1;
    }
    let baseProps;
    if ($[10] !== canonicalSrc || $[11] !== frameClassName || $[12] !== title) {
      baseProps = {
        src: canonicalSrc,
        title,
        sandbox: DEFAULT_SANDBOX,
        allow: "",
        referrerPolicy: "no-referrer",
        loading: "lazy"
      } as React.IframeHTMLAttributes<HTMLIFrameElement> & {
        fetchPriority?: "high" | "low" | "auto" | undefined;
        credentialless?: string | undefined;
      };
      baseProps.fetchPriority = "low";
      baseProps.credentialless = "";
      baseProps.className = frameClassName;
      $[10] = canonicalSrc;
      $[11] = frameClassName;
      $[12] = title;
      $[13] = baseProps;
    } else {
      baseProps = $[13];
    }
    t7 = baseProps;
  }
  const iframeProps = t7;
  if (!canonicalSrc || !iframeProps) {
    let t8;
    if ($[14] !== fallback) {
      t8 = fallback ? <>{fallback}</> : null;
      $[14] = fallback;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    return t8;
  }
  let t8;
  if ($[16] !== frameClassName) {
    t8 = <div className={frameClassName} aria-hidden="true" role="presentation" />;
    $[16] = frameClassName;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const placeholder = t8;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className="tw-text-xs tw-font-semibold tw-uppercase tw-text-iron-300">Untrusted interactive content</span>;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== canonicalSrc || $[20] !== parsedCanonicalUrl) {
    t10 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded-t-md tw-border tw-border-iron-800 tw-bg-iron-950 tw-px-3 tw-py-2" aria-live="polite">{t9}{parsedCanonicalUrl ? <a href={canonicalSrc} target="_blank" rel="noopener noreferrer" className="tw-text-xs tw-font-medium tw-text-primary-300 tw-transition hover:tw-text-primary-300">{parsedCanonicalUrl.hostname}</a> : <span className="tw-text-xs tw-text-iron-400">{canonicalSrc}</span>}</div>;
    $[19] = canonicalSrc;
    $[20] = parsedCanonicalUrl;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const banner = t10;
  let t11;
  if ($[22] !== containerClassName) {
    t11 = ["tw-flex", "tw-flex-col", "tw-h-full", containerClassName].filter(_temp3);
    $[22] = containerClassName;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const containerClasses = t11.join(" ");
  let t12;
  if ($[24] !== iframeProps || $[25] !== isVisible || $[26] !== placeholder) {
    t12 = <div className="tw-min-h-0 tw-flex-1 tw-overflow-hidden">{isVisible ? <iframe {...iframeProps} /> : placeholder}</div>;
    $[24] = iframeProps;
    $[25] = isVisible;
    $[26] = placeholder;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== banner || $[29] !== containerClasses || $[30] !== t12) {
    t13 = <div ref={containerRef} className={containerClasses}>{banner}{t12}</div>;
    $[28] = banner;
    $[29] = containerClasses;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  return t13;
};
export default SandboxedExternalIframe;
function _temp(value) {
  return Boolean(value);
}
function _temp2(entry) {
  return entry.isIntersecting;
}
function _temp3(value_0) {
  return Boolean(value_0);
}